import { useState, useCallback } from 'react';
import { User } from '../types';
import { UserService } from '../services/userService';
import { useActivity } from './useActivity';
import { useAuth } from '../contexts/AuthContext';

export const useUserFollow = () => {
  const { user } = useAuth();
  const { recordFollow } = useActivity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const followUser = useCallback(async (currentUserId: string, targetUserId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const success = await UserService.followUser(currentUserId, targetUserId);
      if (!success) {
        setError('Impossible de suivre cet utilisateur');
      } else {
        // Enregistrer l'activité de follow
        if (user && (user.username || user.displayName)) {
          await recordFollow(
            currentUserId, 
            targetUserId, 
            user.username || user.id, 
            user.displayName || user.username || `User ${user.id.substring(0, 8)}`
          );
        }
      }
      return success;
    } catch (err) {
      setError('Erreur lors du suivi');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, recordFollow]);

  const unfollowUser = useCallback(async (currentUserId: string, targetUserId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const success = await UserService.unfollowUser(currentUserId, targetUserId);
      if (!success) {
        setError('Impossible de ne plus suivre cet utilisateur');
      }
      return success;
    } catch (err) {
      setError('Erreur lors du désabonnement');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const isFollowing = useCallback(async (currentUserId: string, targetUserId: string): Promise<boolean> => {
    try {
      return await UserService.isFollowing(currentUserId, targetUserId);
    } catch (err) {
      console.error('Erreur vérification suivi:', err);
      return false;
    }
  }, []);

  const getSuggestedUsers = useCallback(async (currentUserId: string, limit?: number): Promise<User[]> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.getSuggestedUsers(currentUserId, limit);
    } catch (err) {
      setError('Erreur lors du chargement des suggestions');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getFollowers = useCallback(async (userId: string): Promise<User[]> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.getFollowers(userId);
    } catch (err) {
      setError('Erreur lors du chargement des followers');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getFollowing = useCallback(async (userId: string): Promise<User[]> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.getFollowing(userId);
    } catch (err) {
      setError('Erreur lors du chargement des abonnements');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    followUser,
    unfollowUser,
    isFollowing,
    getSuggestedUsers,
    getFollowers,
    getFollowing,
    loading,
    error
  };
};

export const useUserProfile = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateProfile = useCallback(async (userId: string, updates: Partial<User>): Promise<User | null> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.updateUserProfile(userId, updates);
    } catch (err) {
      setError('Erreur lors de la mise à jour du profil');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadProfilePicture = useCallback(async (userId: string, file: File): Promise<string | null> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.uploadProfilePicture(userId, file);
    } catch (err) {
      setError('Erreur lors de l\'upload de la photo de profil');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadBannerImage = useCallback(async (userId: string, file: File): Promise<string | null> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.uploadBannerImage(userId, file);
    } catch (err) {
      setError('Erreur lors de l\'upload de la bannière');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserById = useCallback(async (userId: string): Promise<User | null> => {
    setLoading(true);
    setError(null);
    try {
      return await UserService.getUserById(userId);
    } catch (err) {
      setError('Erreur lors de la récupération du profil');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateProfile,
    uploadProfilePicture,
    uploadBannerImage,
    getUserById,
    loading,
    error
  };
};
